import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {AxiosError, isAxiosError} from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE, PASSWORD_RESET_REQUEST, SUCCESS_CHECK} from '../components/ToastBottom';
import {AppRoute} from '../models/AppRoute';
import {Address, User} from '../models/mfl';
import {AccountScreenAction, parseAccountScreenActionKey} from '../models/navigationTypes';
import {navigate} from '../navigation/RootNavigation';
import apiService, {convertAppAddressToMFLAddress} from '../transport/apiService';
import {logError} from '../utils/errorLogging';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {logger, serializeError} from '../utils/logger';
import {getCreateOrUpdateUserErrorMessages, hasWeakPasswordError} from '../utils/mfl';
import {publishSessionEvent} from '../utils/sessionSync';

import type {RootStore} from './rootStore';

const SERVER_SESSION_ID = 'SERVER_SESSION_ID';

// Type definitions for API responses
interface EmailCheckResponse {
  can_sign_in: boolean;
  can_sign_up: boolean;
  exists_in_zen: boolean;
  first_name: string;
  status: string;
}

interface LoginResponse {
  needToVerifyEmail?: boolean;
  id: string;
}

interface SignUpUserResponse {
  data: {
    id: string;
    zohoId?: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    email_address: string;
    birth_date: string;
    billing_address: Address;
    rewardsUser?: unknown;
  };
  status?: number;
}

interface CreateUserResponse {
  id: string;
  zohoId?: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email_address: string;
  birth_date: string;
  billing_address: Address;
}

interface UserFormValues {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  billingAddress?: Address;
  optInRewards?: boolean;
  signUpCode?: string;
  passwordValidationBypass?: boolean;
}

export class UserStore {
  rootStore: RootStore;
  // Session Detail (DIS-1026: server-side sessions)
  serverSessionId: string = '';
  isSessionChecked: boolean = false;
  // User detail
  id: string = ''; // MFL ID
  zohoId: string = '';
  firstName: string = '';
  lastName: string = '';
  phoneNumber: string = '';
  emailAddress: string = '';
  password: string = '';
  dateOfBirth?: string; // YYYY-MM-DD format
  billingAddress?: Address;
  showWeakPasswordCheckbox = false;
  postLoginReturnToScreen: AppRoute | [AppRoute, {screen: AppRoute}] | null = null;
  formValues: UserFormValues | null = null;
  // Cached user data from password reset flow (for retry after weak password)
  private _passwordResetUserData: CreateUserResponse | null = null;

  /**
   * This will be set if user has checked opt in rewards when create account but zen account creation did not succeed
   */
  loyaltyPending: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  get name() {
    return `${this.firstName} ${this.lastName}`;
  }

  reset = () => {};

  default = () => {
    runInAction(() => {
      this.serverSessionId = '';
      this.isSessionChecked = false;
      this.firstName = '';
      this.lastName = '';
      this.phoneNumber = '';
      this.emailAddress = '';
      this.password = '';
      this.dateOfBirth = undefined;
      this.billingAddress = undefined;
      this.showWeakPasswordCheckbox = false;
      this.postLoginReturnToScreen = null;
      this.formValues = null;
      this._passwordResetUserData = null;
    });
  };

  get isLoggedIn(): boolean {
    if (Platform.OS === 'web') {
      return this.isSessionChecked && !!this.serverSessionId;
    }
    return !!this.serverSessionId;
  }

  get isUserMissingDetails() {
    return (
      !this.dateOfBirth ||
      this.dateOfBirth === '' ||
      this.dateOfBirth === '1900-01-01' ||
      this.firstName.trim().length === 0 ||
      this.lastName.trim().length === 0 ||
      this.phoneNumber.trim().length === 0 ||
      this.emailAddress.trim().length === 0 ||
      !this.billingAddress ||
      this.billingAddress.lines.length === 0
    );
  }

  setPostLoginReturnToScreen = (
    screen: AppRoute | null,
    params?: {screen: AppRoute; params?: Record<string, string>},
  ) => {
    if (params && !!screen) {
      this.postLoginReturnToScreen = [screen, params];
    } else {
      this.postLoginReturnToScreen = screen;
    }
  };

  logout = async (options?: {callApi?: boolean; broadcast?: boolean}) => {
    const {callApi = true, broadcast = true} = options ?? {};

    // Passive logout (another tab already logged out): the server session is
    // already dead — replaying POST /ecommerce/logout would just 401.
    if (callApi) {
      await apiService.logout();
    }

    await removeValueFromStorage(SERVER_SESSION_ID);

    this.default();

    // Generate a new device ID on logout, per MFL request.
    await this.rootStore.globalStore.resetDeviceId();

    await this.rootStore.logoutUser();

    if (broadcast) {
      publishSessionEvent({type: 'logout', at: Date.now()});
    }

    return null;
  };

  checkEmailNew = async (email: string) => {
    this.emailAddress = email;

    return apiService
      .checkEmail2(email)
      .then((data: EmailCheckResponse) => {
        return {
          canSignIn: data.can_sign_in,
          canSignUp: data.can_sign_up,
          existsInZen: data.exists_in_zen,
          firstName: data.first_name,
          status: data.status,
        };
      })
      .catch(err => logger.error('UserStore', 'Error checking email (v2)', serializeError(err)));
  };

  login = async (body: {email: string; password: string}) => {
    try {
      const data = (await apiService.login2(body)) as LoginResponse;
      logger.debug('UserStore', 'Login v2 successful', {
        email: body.email,
        needToVerifyEmail: data.needToVerifyEmail,
      });

      if (data.needToVerifyEmail) {
        // navigate to email verification screen

        runInAction(() => {
          this.emailAddress = body?.email;
          this.password = body?.password; // needed for /verify-email endpoint
        });

        navigate(AppRoute.ROOT, {
          screen: AppRoute.MAIN,
          params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
        });
      } else {
        // Session stored automatically by sessionIdExtractionInterceptor
        await this.rootStore.login();
        if (this.isUserMissingDetails) {
          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
          });
          return false;
        }

        return true;
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        // TODO surface error details to user
      } else {
        logError(err, 'UserStore.login');
      }
      return false;
    }
  };

  verifyUserEmail = async (code: string) => {
    try {
      const data = await apiService.verifyEmail(this.emailAddress, this.password, code);

      if (data) {
        // Session stored automatically by sessionIdExtractionInterceptor
        this.rootStore.invalidateLinesStores();
        await this.rootStore.initialiseUserData();

        if (this.isUserMissingDetails) {
          navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {screen: AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS},
          });
          return false;
        }

        return true;
      }
      return false;
    } catch (error) {
      logError(error, 'UserStore.verifyUserEmail');
      return false;
    }
  };

  afterLogin = (fallback?: AppRoute) => {
    if (this.postLoginReturnToScreen) {
      if (Array.isArray(this.postLoginReturnToScreen)) {
        navigate(...this.postLoginReturnToScreen);
      } else {
        navigate(this.postLoginReturnToScreen);
      }
      this.setPostLoginReturnToScreen(null);
    } else if (fallback) {
      navigate(fallback);
    } else {
      const navigationTarget = this.rootStore.shopSelectionStore?.homeAppRoute;
      navigate(navigationTarget);
    }
  };

  afterUpdateUser = () => {
    this.afterLogin();
  };

  /**
   * Store the server-issued session ID received in x-session-id response header.
   * Called by sessionIdExtractionInterceptor (fire-and-forget from interceptor context).
   * MobX update is synchronous; EncryptedStorage write is async (native only).
   */
  storeServerSessionId = async (sessionId: string): Promise<void> => {
    runInAction(() => {
      this.serverSessionId = sessionId;
      this.isSessionChecked = true;
    });
    // A live session now exists — clear any prior dead-session logout latch so
    // a future session death can fire logout again. Session-death responses
    // carry no x-session-id, so this is never reached during a 401 burst.
    apiService.resetUnauthorizedGuard();
    if (Platform.OS !== 'web') {
      await setValueToStorage(SERVER_SESSION_ID, sessionId);
    }
    // Announce the new session to sibling tabs (web only — no-op on native).
    // Deliberately NOT published from restoreSession: cold-start restores
    // happen in every tab independently and would just add broadcast noise.
    publishSessionEvent({type: 'login', at: Date.now()});
  };

  /**
   * Restore session on app startup.
   * - Web: validates cookie via GET /ecommerce/session (cookie sent automatically)
   * - Native: reads SERVER_SESSION_ID from EncryptedStorage
   */
  restoreSession = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      try {
        await apiService.checkSession();
        runInAction(() => {
          // Sentinel value — actual auth is the mw_session cookie.
          this.serverSessionId = 'web-session-active';
          this.isSessionChecked = true;
        });
        // Live session restored — clear any stale logout latch.
        apiService.resetUnauthorizedGuard();
        return true;
      } catch {
        runInAction(() => {
          this.serverSessionId = '';
          this.isSessionChecked = true;
        });
        return false;
      }
    }

    // Native: restore from EncryptedStorage
    const sessionId = await getValueFromStorage(SERVER_SESSION_ID);
    runInAction(() => {
      this.serverSessionId = sessionId || '';
      this.isSessionChecked = true;
    });
    if (sessionId) {
      // Live session restored — clear any stale logout latch.
      apiService.resetUnauthorizedGuard();
    }
    return !!sessionId;
  };

  storeUserInfo = (data: {
    id: string; // MFL ID
    first_name: string;
    last_name: string;
    phone_number: string;
    email_address: string;
    birth_date: string;
    billing_address: Address;
    zohoId?: string;
  }) => {
    try {
      if (window?.$zoho?.salesiq?.visitor) {
        // Despite Zoho documentation which says we to use the .ready() method,
        // it doesn't work, so we ignore it and just set the values directly.
        window.$zoho.salesiq.visitor.email(data.email_address);
        window.$zoho.salesiq.visitor.contactnumber(data.phone_number);
        window.$zoho.salesiq.visitor.name(`${data.first_name} ${data.last_name}`);
      }
    } catch (error) {
      logError(error, 'UserStore.retrieveUserInfoV2.zohoIntegration');
    }
    runInAction(() => {
      this.id = data.id;
      this.firstName = data.first_name;
      this.lastName = data.last_name;
      this.phoneNumber = data.phone_number;
      this.emailAddress = data.email_address;
      this.dateOfBirth = data.birth_date; // Already in YYYY-MM-DD format
      this.billingAddress = data.billing_address;
      this.zohoId = data.zohoId || '';
    });
  };

  retrieveUserInfo = async () => {
    try {
      const data = await apiService.getUserInfo();
      logger.debug('UserStore', 'Retrieved user info', {
        userId: data?.id,
        email: data?.email,
      });
      if (data) {
        this.storeUserInfo(data);
        return data;
      }
      return null;
    } catch (error) {
      logger.error('UserStore', 'Unable to retrieve user info', {error});
      return null;
    }
  };

  retrieveUserInfoV2 = async (options?: {retryable?: boolean; zohoId?: string}) => {
    try {
      const user = await apiService.getUserInfoV2(options);
      this.storeUserInfo({
        id: user.id,
        zohoId: user.zohoId,
        first_name: user.firstName,
        last_name: user.lastName,
        phone_number: user.phoneNumber,
        email_address: user.email,
        birth_date: user.dateOfBirth, // Already converted to YYYY-MM-DD in transformer
        billing_address: user.billingAddress,
      });
    } catch (err) {
      logError(err, 'UserStore.retrieveUserInfoV2');
      throw err;
    }
  };

  handleCreateUpdateUserError = (err: unknown, isCreate: boolean = false) => {
    let errorMessage = `Unable to ${isCreate ? 'create' : 'update'} user. Please try again.`;
    let isWeakPassword = false;

    // Runtime validation: Check if error is an AxiosError
    if (!isAxiosError(err)) {
      logger.error('UserStore', 'Non-Axios error in user operation', serializeError(err));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: errorMessage,
        visibilityTime: 8000,
      });
      return;
    }

    // Safe to access Axios-specific properties after type guard
    const errors = err.response?.data?.errors;
    const status = err.response?.data?.status;

    if (errors) {
      isWeakPassword = hasWeakPasswordError(errors);
      if (!isWeakPassword) {
        errorMessage = getCreateOrUpdateUserErrorMessages(errors);
      }
    }

    // Check for PASSWORD_TOO_WEAK status
    if (status === 'PASSWORD_TOO_WEAK') {
      isWeakPassword = true;
    }

    if (isWeakPassword) {
      runInAction(() => (this.showWeakPasswordCheckbox = true));
      errorMessage = 'This password is too weak or may be compromised';
    }

    Toast.show({
      type: ERROR_MESSAGE,
      text1: errorMessage,
      visibilityTime: 8000,
    });
  };

  updateUserInfo = async (
    action: keyof typeof AccountScreenAction | AccountScreenAction = 'UNKNOWN',
    values: User,
    successCallback = () => {},
    errorCallback: (error?: {status: string; message: string}) => void = () => {},
    options?: {
      showSuccessToast?: boolean;
      verificationCode?: string;
    },
  ) => {
    const {showSuccessToast = true, verificationCode} = options || {};
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const actionString = parseAccountScreenActionKey(action) || 'UNKNOWN';
      const response = await apiService.updateUserInfo(actionString, values, {
        verificationCode,
      });
      this.storeUserInfo(response.data);
      if (showSuccessToast) {
        Toast.show({
          type: SUCCESS_CHECK,
          onHide: successCallback,
        });
      } else {
        successCallback();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const e = error.response?.data;
        errorCallback({
          status: e?.status,
          message: e?.message,
        });
      } else {
        errorCallback();
      }
      this.handleCreateUpdateUserError(error);
    }
  };

  updateBillingAddress = async (address: Address) => {
    logger.debug('UserStore', 'Updating billing address', {
      postalCode: address.postal_code,
      locality: address.locality,
    });
    return new Promise<boolean>((resolve, reject) => {
      this.updateUserInfo(
        'UPDATE_BILLING_ADDRESS',
        {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.emailAddress,
          phoneNumber: this.phoneNumber,
          dateOfBirth: this.dateOfBirth,
          billingAddress: {...address},
        },
        () => resolve(true),
        () => reject(),
        {
          showSuccessToast: false,
        },
      );
    });
  };

  /** This create account is for the loyalty work, capturing the form data
   * while the user verifies their phone number.
   */
  updateUserFormValues = (values: UserFormValues) => {
    runInAction(() => {
      this.formValues = values;
    });
  };

  signUpUser = async (code: string) => {
    const selectedStore = this.rootStore.shopSelectionStore.selectedStoreSummary;
    if (!selectedStore) {
      logger.error('UserStore', 'No selected store for sign up');
      return false;
    }
    try {
      const response = (await apiService.createUserNew(
        this.formValues,
        {
          id: selectedStore.id,
          name: selectedStore.name,
        },
        !!this.formValues?.optInRewards,
        code,
        this.formValues?.signUpCode && this.formValues.signUpCode.trim() !== ''
          ? this.formValues.signUpCode
          : undefined,
      )) as SignUpUserResponse;

      if (!response || !response.data) {
        logger.error('UserStore', 'Invalid sign up response', {
          status: response?.status,
        });
        return false;
      }

      const data = response.data;

      this.storeUserInfo(data);
      // Session stored automatically by sessionIdExtractionInterceptor
      this.rootStore.invalidateLinesStores();
      await this.rootStore.initialiseUserDataNonBlocking({zohoId: data.zohoId});
      if (data.rewardsUser) {
        // This won't be sent in the case of not opting in to rewards
        this.rootStore?.rewardsStore?.loadRewards(data.rewardsUser);
      } else if (this.formValues?.optInRewards) {
        /**
         * if opt in rewards is checked but zen account wasn't created. data.rewardsUser is null
         */
        runInAction(() => {
          this.loyaltyPending = true;
        });
      }

      // DIS-1537: signUpUser bypasses login()/initialiseUserData()'s store re-init,
      // so the MobX one-shot loads behind the home "What's New" carousel and the
      // "Exclusive Offers" section keep their pre-signup (guest/empty) state until the
      // app is force-killed. Re-trigger them now that the session + rewards card are set:
      // loadDepartments() reassigns rangeStore.departments so the observing
      // DepartmentCarousel re-fires loadRangeLines(), and refreshOffers() refetches offers
      // with the new card (reset() alone won't re-fire the observed onBecomeObserved).
      void this.rootStore.rangeStore.loadDepartments();
      void this.rootStore.offersStore.refreshOffers();

      return true;
    } catch (error) {
      logError(error, 'UserStore.signUpUser');
      return false;
    }
  };

  createUser = async (values: User, successCallback = () => {}, errorCallback = () => {}) => {
    try {
      runInAction(() => {
        this.showWeakPasswordCheckbox = false;
      });
      const data = (await apiService.createUser(values)) as CreateUserResponse;
      this.storeUserInfo(data);
      // Session stored automatically by sessionIdExtractionInterceptor
      this.rootStore.invalidateLinesStores();
      await this.rootStore.initialiseUserData();
      Toast.show({
        onHide: successCallback,
      });
    } catch (error) {
      errorCallback();
      this.handleCreateUpdateUserError(error);
    }
  };

  deleteUser = async (nav?: () => void) => {
    try {
      await apiService.deleteUser(this.emailAddress, this.phoneNumber, this.lastName);
      Toast.show({
        onHide: nav ? () => nav() : undefined,
      });
    } catch (err: unknown) {
      Toast.show({
        type: ERROR_MESSAGE,
        text1: err instanceof Error ? err.message : 'Something went wrong. Please try again later.',
        visibilityTime: 8000,
      });
      logError(err, 'UserStore.deleteUser');
      throw err;
    }
  };

  initiatePasswordReset = async (email: string, nav: () => void) => {
    try {
      await apiService.initiatePasswordReset({email_address: email});
      Toast.show({
        type: PASSWORD_RESET_REQUEST,
        visibilityTime: 3000,
        onHide: nav || undefined,
      });
    } catch (err) {
      logError(err, 'UserStore.initiatePasswordReset');
    }
  };

  resetPassword = async (
    {
      password,
      passwordValidationBypass = false,
    }: {password: string; passwordValidationBypass: boolean},
    token: string,
  ): Promise<{success: boolean; message?: string}> => {
    // Skip session establishment if already logged in (e.g., retry after weak password error)
    // Token is consumed on first use, so we reuse the session from previous attempt
    let userData: CreateUserResponse;

    if (!this.isLoggedIn || !this._passwordResetUserData) {
      // First attempt: establish session with token
      // Session stored automatically by sessionIdExtractionInterceptor
      try {
        await apiService.establishSessionWithToken(token);
      } catch (_err) {
        return {
          success: false,
          message: 'Invalid password reset token. Please request a new password reset.',
        };
      }

      // Get user details
      try {
        userData = await apiService.getUserInfo();
        this.storeUserInfo(userData);
        // Cache for potential retry (weak password scenario)
        this._passwordResetUserData = userData;
      } catch (_err) {
        return {
          success: false,
          message: 'Failed to update user. Please try again.',
        };
      }
    } else {
      // Retry attempt: use cached user data from first attempt
      userData = this._passwordResetUserData;
    }

    // Update user with new password
    try {
      await apiService.updateUserInfo(
        'RESET_PASSWORD',
        {
          ...userData,
          password: password,
          bypass_compromised_password_validation: passwordValidationBypass,
        },
        {
          skipConvert: true,
        },
      );
      // Clear cached data on success
      this._passwordResetUserData = null;
      return {
        success: true,
      };
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.data?.status === 'PASSWORD_TOO_WEAK') {
          runInAction(() => (this.showWeakPasswordCheckbox = true));
          return {
            message: 'Please enter a strong password with a mix of letters, numbers and symbols',
            success: false,
          };
        }
        if (err.response?.data?.errors) {
          for (const error of err.response.data.errors) {
            switch (error.code.toLowerCase()) {
              case 'password_too_weak': {
                runInAction(() => (this.showWeakPasswordCheckbox = true));
                return {
                  message:
                    'Please enter a strong password with a mix of letters, numbers and symbols',
                  success: false,
                };
              }
            }
          }
        }
      }
      return {
        success: false,
        message: 'Failed to update password. Please try again.',
      };
    }
  };

  /**
   * Complete full sign up from email link
   * @param values
   * @param _successCallback - Currently unused, reserved for future use
   * @param errorCallback
   */
  rewardsFullSignup = async (
    values: UserFormValues,
    _successCallback = () => {},
    errorCallback = () => {},
  ) => {
    const billingAddress = convertAppAddressToMFLAddress(values.billingAddress);

    try {
      await apiService.convertLiteUser({
        email: this.emailAddress,
        password: values.password,
        billing_address: billingAddress,
        bypass_compromised_password_validation: values.passwordValidationBypass,
      });

      runInAction(() => {
        this.password = values.password; // needed for /verify-email endpoint
      });
      await apiService.rewardsVerifyExistingUserEmail({
        emailAddress: this.emailAddress,
      });

      navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {screen: AppRoute.ACCOUNT_EMAIL_VERIFICATION},
      });
    } catch (error: unknown) {
      logger.error('UserStore', 'Rewards full signup error', {error});

      if ((error as AxiosError)?.response?.status === 404) {
        // unknown error (user not found in zen)
        errorCallback();
      } else {
        errorCallback();
        this.handleCreateUpdateUserError(error, true);
      }
    }
  };
}
